import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PushNotificationSettings from "@/components/game/PushNotificationSettings";
import { useCurrentLang } from "@/utils/i18n";
import ambientMusic from "@/utils/AmbientMusic";

// Direct imports for the critical first-load path (instant FCP)
import AuthPage from "@/pages/AuthPage";
import LandingPage from "@/pages/LandingPage";

// Code splitting: lazy loaded chunks for heavyweight and secondary areas
const MainMenu = lazy(() => import("@/pages/MainMenu"));
const CharacterCreation = lazy(() => import("@/pages/CharacterCreation"));
const GameView = lazy(() => import("@/pages/GameView"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const Hall = lazy(() => import("@/pages/Hall"));
const HallDetail = lazy(() => import("@/pages/HallDetail"));
const GlobalCollectionPage = lazy(() => import("@/pages/GlobalCollectionPage"));
const CommunityWorlds = lazy(() => import("@/pages/CommunityWorlds"));
const CommunityWorldEditor = lazy(() => import("@/pages/CommunityWorldEditor"));
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/legal/TermsOfService"));
const CookiePolicy = lazy(() => import("@/pages/legal/CookiePolicy"));
const Contact = lazy(() => import("@/pages/legal/Contact"));
const About = lazy(() => import("@/pages/legal/About"));

const API = process.env.REACT_APP_BACKEND_URL;

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c]">
    <div className="text-center space-y-3">
      <div className="w-10 h-10 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin mx-auto" />
      <p className="font-heading text-[11px] uppercase tracking-[0.25em] text-[#cfb53b]/80">
        Arcane Chronicles
      </p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <PageLoader />;
  }
  return user ? children : <Navigate to="/auth" replace state={{ returnTo: location.pathname + location.search + location.hash }} />;
};

// Iter 91/92 — `/` is a public landing page for anonymous visitors.
// Authenticated users see the MainMenu directly so the existing in-app
// navigation (logout → /, sidebar logo → /) keeps working as before.
// Iter 92 — when `?draft=1` is present we ALWAYS show the LandingPage
// (even to admins) so the admin CMS preview iframe works correctly.
const HomeRoute = () => {
  const { user, loading } = useAuth();
  const isDraftPreview = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("draft") === "1";
  if (loading) {
    return <PageLoader />;
  }
  if (isDraftPreview) return <LandingPage />;
  return user ? (
    <Suspense fallback={<PageLoader />}>
      <MainMenu />
    </Suspense>
  ) : <LandingPage />;
};

function App() {
  const [currentLang] = useCurrentLang();

  // Load admin-managed audio library once at app boot
  useEffect(() => {
    ambientMusic.loadLibrary(API || "");

    // Initialize CSS variable and data attribute for story font on startup
    const savedFont = localStorage.getItem('ac_story_font') || 'cursive';
    const fontName = savedFont === 'classic' ? "'EB Garamond', 'Georgia', serif" : "'Dancing Script', cursive";
    document.documentElement.setAttribute('data-story-font', savedFont);
    document.documentElement.style.setProperty('--story-font', fontName);
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/auth" element={<AuthPage />} />
              {/* Public legal / info pages — no auth required */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              {/* Hall of Stories — public showcase */}
              <Route path="/hall" element={<Hall />} />
              <Route path="/hall/:pid" element={<HallDetail />} />
              <Route path="/global-collection" element={<GlobalCollectionPage />} />
              <Route path="/" element={<HomeRoute />} />
              <Route path="/menu" element={<ProtectedRoute><MainMenu /></ProtectedRoute>} />
              <Route path="/create" element={<ProtectedRoute><CharacterCreation /></ProtectedRoute>} />
              <Route path="/worlds" element={<ProtectedRoute><CommunityWorlds /></ProtectedRoute>} />
              <Route path="/worlds/:worldId/edit" element={<ProtectedRoute><CommunityWorldEditor /></ProtectedRoute>} />
              <Route path="/game/:gameId" element={<ProtectedRoute><GameView /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" theme="dark" richColors />
          <PWAInstallPrompt lang={currentLang} />
          <PushNotificationSettings variant="banner" lang={currentLang} />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
